// apps/saas/src/utils/backendRedirect.ts

/**
 * Backend response shape returned with HTTP 301 when a catalog resource has
 * been deleted or moved. The redirectTo target tells us where to send the
 * client.
 */
export interface BackendRedirectResponse {
  redirectTo: {
    type: 'category' | 'root';
    slug: string | null;
  };
  reason: string;
}

export const isBackendRedirect = (
  value: unknown,
): value is BackendRedirectResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  if (typeof v.reason !== 'string') {
    return false;
  }
  const redirectTo = v.redirectTo;
  if (!redirectTo || typeof redirectTo !== 'object') {
    return false;
  }
  const r = redirectTo as Record<string, unknown>;
  if (r.type !== 'category' && r.type !== 'root') {
    return false;
  }
  return r.slug === null || typeof r.slug === 'string';
};

/**
 * Resolve a backend redirect payload to a target path.
 *
 * Returns null when:
 *  - type is unknown,
 *  - type === 'category' but slug is empty/null,
 *  - resolved path equals currentPath (self-loop guard).
 *
 * The query string of currentPath is intentionally discarded — filters and
 * sorting were tied to the old resource.
 */
export const resolveBackendRedirect = (
  payload: BackendRedirectResponse,
  currentPath: string,
): string | null => {
  const { type, slug } = payload.redirectTo;

  let target: string | null = null;
  if (type === 'category' && slug) {
    target = `/c/${slug}`;
  } else if (type === 'root') {
    target = '/shop';
  }

  if (!target) {
    return null;
  }

  const currentPathname = currentPath.split('?')[0];
  if (target === currentPathname) {
    return null;
  }

  return target;
};
